import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { confirmEmail } from '@/api/auth';
import { useAuth } from '@/contexts/AuthContext';

export function ConfirmEmailPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!code) {
      setStatus('error');
      setMessage('Invalid confirmation link');
      return;
    }

    handleConfirmation();
  }, [code]);

  const handleConfirmation = async () => {
    if (!code) {
      setStatus('error');
      setMessage('Invalid confirmation code');
      return;
    }

    try {
      const response = await confirmEmail(code);

      if (response.success) {
        // Refresh user data to update authentication state
        await refreshUser();
        
        // Immediately redirect after successful confirmation (brain dead simple!)
        // No need to show UI - just go straight to the right place
        const redirectUrl = response.redirect || '/onboarding';
        navigate(redirectUrl, { replace: true });
        return; // Don't set UI state, just redirect
      } else {
        setStatus('error');
        setMessage(response.error || 'Failed to confirm email');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to confirm email. Please try again.');
    }
  };


  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <CardTitle className="text-2xl text-center mb-4">Confirming Email...</CardTitle>
            <p className="text-gray-600 text-center">
              Please wait while we confirm your email address.
            </p>
          </>
        );


      case 'error':
        return (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-center mb-4">Confirmation Failed</CardTitle>
            <Alert className="mb-6" variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                {message}
              </AlertDescription>
            </Alert>
            <div className="text-center space-y-3">
              <p className="text-sm text-gray-600">
                The confirmation link may be invalid or expired.
              </p>
              <div className="space-x-3">
                <Button variant="outline" onClick={() => navigate('/auth/login')}>
                  Go to Login
                </Button>
                <Button variant="outline" onClick={() => navigate('/auth/forgot-password')}>
                  Reset Password
                </Button>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/doploy-logo.png"
            alt="Doploy"
            className="h-8 mx-auto mb-4"
          />
        </div>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardHeader className="text-center pb-4">
            {renderContent()}
          </CardHeader>

          {status !== 'loading' && (
            <CardContent className="text-center pt-0">
              <p className="text-xs text-gray-500">
                Need help? Contact us at{' '}
                <a
                  href="mailto:support@doploy.io"
                  className="text-blue-600 hover:text-blue-800"
                >
                  support@doploy.io
                </a>
              </p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
